// src/Pages/PharmacistDashboard/PharmacistMessagingHub.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
  Divider,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Send as SendIcon,
  Message as MessageIcon,
  Delete as DeleteIcon,
  Mail as InboxIcon,
  MailOutline as SentIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { messageMockData } from '../../data/pharmacistMessagingMockData'; // Mock data

const PharmacistMessagingHub = () => {
  const [messages, setMessages] = useState(messageMockData);
  const [selectedCategory, setSelectedCategory] = useState('Inbox');
  const [filterSender, setFilterSender] = useState('');
  const [newMessage, setNewMessage] = useState({
    recipient: '',
    subject: '',
    content: '',
    priority: 'Normal',
  });
  const [notification, setNotification] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleFilterSenderChange = (event) => {
    setFilterSender(event.target.value);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewMessage((prevMessage) => ({
      ...prevMessage,
      [name]: value,
    }));
  };

  const handleSendMessage = () => {
    if (newMessage.recipient && newMessage.content) {
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: messages.length + 1,
          ...newMessage,
          sender: 'Pharmacist',
          date: new Date().toLocaleDateString(),
          status: 'Sent',
        },
      ]);
      setNotification({
        message: 'Message sent successfully',
        severity: 'success',
      });
      setNewMessage({
        recipient: '',
        subject: '',
        content: '',
        priority: 'Normal',
      });
    } else {
      setNotification({
        message: 'Please fill in all fields',
        severity: 'error',
      });
    }
  };

  const handleDeleteMessage = (id) => {
    setMessages((prevMessages) =>
      prevMessages.filter((message) => message.id !== id)
    );
    setNotification({ message: 'Message deleted', severity: 'info' });
  };

  const filteredMessages = messages.filter(
    (message) =>
      (selectedCategory === 'Inbox'
        ? message.status !== 'Sent'
        : message.status === 'Sent') &&
      (filterSender ? message.sender === filterSender : true)
  );

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant='h4' gutterBottom>
        Messaging and Communication Hub
      </Typography>

      {/* Filters and Categories */}
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} md={3}>
          <Button
            variant={selectedCategory === 'Inbox' ? 'contained' : 'outlined'}
            fullWidth
            startIcon={<InboxIcon />}
            onClick={() => handleCategoryChange('Inbox')}
          >
            Inbox
          </Button>
        </Grid>
        <Grid item xs={12} md={3}>
          <Button
            variant={selectedCategory === 'Sent' ? 'contained' : 'outlined'}
            fullWidth
            startIcon={<SentIcon />}
            onClick={() => handleCategoryChange('Sent')}
          >
            Sent Messages
          </Button>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Filter by Sender</InputLabel>
            <Select
              value={filterSender}
              onChange={handleFilterSenderChange}
              label='Filter by Sender'
            >
              <MenuItem value=''>All Senders</MenuItem>
              <MenuItem value='Doctor'>Doctors</MenuItem>
              <MenuItem value='Supplier'>Suppliers</MenuItem>
              <MenuItem value='Staff'>Pharmacy Staff</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Messages List */}
      <Paper
        elevation={2}
        sx={{ maxHeight: isMobile ? 300 : 500, overflow: 'auto', padding: 2 }}
      >
        <List>
          {filteredMessages.map((message) => (
            <ListItem
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <ListItemText
                primary={`From: ${message.sender} | Subject: ${message.subject}`}
                secondary={`Date: ${message.date}`}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  color='error'
                  onClick={() => handleDeleteMessage(message.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </ListItem>
          ))}
        </List>
      </Paper>

      <Divider sx={{ my: 4 }} />

      {/* Compose Message */}
      <Typography variant='h6' gutterBottom>
        Compose New Message
      </Typography>
      <Paper elevation={2} sx={{ padding: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Select Recipient</InputLabel>
              <Select
                name='recipient'
                value={newMessage.recipient}
                onChange={handleInputChange}
              >
                <MenuItem value=''>Select Recipient</MenuItem>
                <MenuItem value='Doctor'>Doctor</MenuItem>
                <MenuItem value='Supplier'>Supplier</MenuItem>
                <MenuItem value='Staff'>Pharmacy Staff</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='Subject'
              name='subject'
              value={newMessage.subject}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                name='priority'
                value={newMessage.priority}
                onChange={handleInputChange}
              >
                <MenuItem value='Normal'>Normal</MenuItem>
                <MenuItem value='Urgent'>Urgent</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Message Content'
              name='content'
              value={newMessage.content}
              multiline
              rows={4}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant='contained'
              color='primary'
              fullWidth
              startIcon={<SendIcon />}
              onClick={handleSendMessage}
            >
              Send Message
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Snackbar for Feedback */}
      {notification && (
        <Snackbar
          open={Boolean(notification)}
          autoHideDuration={4000}
          onClose={() => setNotification(null)}
        >
          <Alert
            onClose={() => setNotification(null)}
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default PharmacistMessagingHub;
